/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Radio, RadioGroup, Typography } from "@material-ui/core";
import { FormikConfig } from "formik";
import { getPropertyReinstatementEmailById, updatePropertyReinstatement, updatePropertyReinstatementVariables } from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { CCEmailForm } from "./cc-email-form";

const SUCCESS_MESSAGE = "Success";
const ERROR_MESSAGE = "Error updating email, please support@havenconnect.com for assistance.";

const PROPERTY = gql`
  query getPropertyReinstatementEmailById($id: ID!) {
    property(id: $id) {
      id
      reinstatementEmail
      reinstatementEmailFrequency
    }
  }
`;

const UPDATE_PROPERTY = gql`
  mutation updatePropertyReinstatement($id: ID!, $data: PropertyUpdateInput!) {
    updatePropertyReinstatement(id: $id, data: $data) {
      id
      reinstatementEmail
      reinstatementEmailFrequency
    }
  }
`;

interface Props {
  propertyId: string;
}

export const ReinstatementEmailForm : React.FC<Props> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();

  const [updatePropertySettings] =
    useMutation<updatePropertyReinstatement, updatePropertyReinstatementVariables>(
      UPDATE_PROPERTY,
      { refetchQueries: ["getPropertyReinstatementEmailById"] },
    );
  const { data, loading, error } = useQuery<getPropertyReinstatementEmailById>(PROPERTY, {
    fetchPolicy: "cache-first",
    variables: { id: propertyId },

  });

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  const { property } = data;

  const updateEmail: FormikConfig<{email: string}>["onSubmit"] = (values) => {

    updatePropertySettings({
      variables: {
        id: property.id,
        data: {
          reinstatementEmail: values.email,
        },
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: ERROR_MESSAGE, status: "error", duration: 4000 });
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Reinstatement Email</Typography>
        <Typography variant="subtitle2">This email will receive reinstatements requests from applicants who have been removed.</Typography>
      </Grid>
      <Grid item xs={12} sm={6} xl={4}>
        <CCEmailForm
          initialEmail={property.reinstatementEmail || ""}
          onSubmit={updateEmail}
          label="Reinstatement Email"
          placeholder="Enter a Reinstatement Email"
        />
        <FormHelperText>
          When not set the property&apos;s email will be used.
        </FormHelperText>
      </Grid>

      <Grid item xs={12}>
        <FormControl variant="outlined">
          <FormLabel>Receive Reinstatement Requests</FormLabel>
          <RadioGroup>
            {[
              { label: "Instant", value: "instant" },
              { label: "Daily", value: "daily" },
            ].map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                label={option.label}
                control={<Radio checked={option.value === property.reinstatementEmailFrequency}/>}
                onChange={() => updatePropertySettings({
                  variables: {
                    id: property.id,
                    data: {
                      reinstatementEmailFrequency: option.value,
                    },
                  },
                })
                  .then(() => {
                    openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
                  })}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
    </Grid>
  );
};
